import { useNavigate } from "react-router-dom";
import { Archive, ArchiveRestore, Copy, FolderOpen, MoreVertical, Pencil, Share2, Tag, Trash2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AccessBadge } from "@/components/teacher/ShareDialog";
import { LikeButton, TagChips } from "@/components/teacher/Social";
import { LockButton, LockedBadge, useBlocked } from "@/components/teacher/SiteAdmin";
import { ContentBadges } from "@/components/teacher/ContentBadges";

const Menu = ({ pool, blocked, onRename, onDuplicate, onArchive, onDelete, onShare, onTag }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
      <button className="p-1.5 -mr-1.5 rounded-md hover:bg-[rgba(71,85,105,0.45)]" data-testid={`pool-menu-${pool.id}`} aria-label="Havuz işlemleri">
        <MoreVertical className="h-5 w-5" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
      {pool.mine !== false && <DropdownMenuItem onClick={() => onRename(pool)} data-testid="pool-rename-action"><Pencil className="h-4 w-4 mr-2" />Yeniden adlandır</DropdownMenuItem>}
      <DropdownMenuItem disabled={blocked} onClick={() => onDuplicate(pool)} data-testid="pool-duplicate-action"><Copy className="h-4 w-4 mr-2" />Çoğalt</DropdownMenuItem>
      {pool.mine !== false && <DropdownMenuItem onClick={() => onTag(pool)} data-testid="pool-tag-action"><Tag className="h-4 w-4 mr-2" />Öğrenim düzeyi / etiket</DropdownMenuItem>}
      {pool.mine !== false && <DropdownMenuItem onClick={() => onShare(pool)} data-testid="pool-share-action"><Share2 className="h-4 w-4 mr-2" />Paylaş</DropdownMenuItem>}
      {pool.mine !== false && <DropdownMenuItem onClick={() => onArchive(pool)} data-testid="pool-archive-action">
        {pool.archived ? <ArchiveRestore className="h-4 w-4 mr-2" /> : <Archive className="h-4 w-4 mr-2" />}
        {pool.archived ? "Arşivden çıkar" : "Arşivle"}
      </DropdownMenuItem>}
      {pool.mine !== false && <>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onDelete(pool)} className="text-red-400" data-testid="pool-delete-action"><Trash2 className="h-4 w-4 mr-2" />Sil</DropdownMenuItem>
      </>}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const PoolCard = ({ pool, onReload, ...actions }) => {
  const navigate = useNavigate();
  const blocked = useBlocked()(pool);
  return (
    <div
      className={`panel p-5 flex flex-col gap-3 cursor-pointer hover:border-[#FACC15]/60 hover:bg-white/10 transition-[background-color,border-color] duration-200 fade-in ${pool.locked ? "opacity-60" : ""}`}
      onClick={() => navigate(`/ogretmen/havuzlar/${pool.id}`)}
      data-testid={`pool-card-${pool.id}`}
    >
      <div className="flex items-center gap-2">
        <p className="eyebrow text-[#FACC15] flex-1 truncate" data-testid="pool-card-count">{pool.question_count} soru</p>
        <AccessBadge item={pool} />
        {pool.is_sample && <Badge className="bg-[#34D399]/15 text-[#6EE7B7] border border-[#34D399]/40">Örnek</Badge>}
        {pool.archived && <Badge variant="outline" className="text-slate-300">Arşivde</Badge>}
        <LockedBadge item={pool} />
        <Menu pool={pool} blocked={blocked} {...actions} />
      </div>
      <div>
        <h3 className="font-display text-xl font-bold leading-tight break-words" data-testid="pool-card-name">{pool.name}</h3>
        {pool.owner_name && <p className="text-xs text-slate-400 mt-0.5" data-testid="pool-card-owner">Oluşturan: <span className="text-slate-300">{pool.owner_name}</span></p>}
      </div>
      <TagChips item={pool} />
      <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <LikeButton kind="pools" item={pool} />
        <ContentBadges kind="pools" item={pool} onChange={onReload} compact />
      </div>
      <div className="mt-auto pt-1 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
        <Button size="sm" onClick={() => navigate(`/ogretmen/havuzlar/${pool.id}`)} data-testid={`pool-open-${pool.id}`}><FolderOpen className="h-4 w-4 mr-1" />Aç</Button>
        <Button size="sm" variant="outline" disabled={blocked} onClick={() => actions.onDuplicate(pool)} data-testid={`pool-duplicate-${pool.id}`}><Copy className="h-4 w-4 mr-1" />Çoğalt</Button>
        <LockButton kind="pools" item={pool} onChange={onReload} />
      </div>
    </div>
  );
};
